import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  DollarSign, 
  ChevronRight,
  Star,
  Zap,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { Decision } from '../types/game';
import { useGameStore } from '../store/gameStore';

interface DecisionCardProps {
  decision: Decision;
}

const DecisionCard: React.FC<DecisionCardProps> = ({ decision }) => {
  const { openDecisionModal } = useGameStore();

  const handleCardClick = () => {
    openDecisionModal(decision);
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      health: 'from-red-500 to-pink-500',
      career: 'from-blue-500 to-cyan-500',
      relationships: 'from-purple-500 to-indigo-500',
      education: 'from-violet-500 to-purple-500',
      finance: 'from-emerald-500 to-green-500',
      lifestyle: 'from-orange-500 to-yellow-500',
    };
    return colors[category as keyof typeof colors] || 'from-slate-500 to-slate-600';
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      health: '❤️',
      career: '💼',
      relationships: '👥',
      education: '🎓',
      finance: '💰',
      lifestyle: '🧠',
    };
    return icons[category as keyof typeof icons] || '❓';
  };

  const hasExpensiveOptions = decision.options.some(option => option.cost && option.cost > 10000);
  const hasCheapOptions = decision.options.some(option => !option.cost || option.cost < 1000);

  return (
    <motion.div
      whileHover={{ 
        scale: 1.02, 
        y: -8,
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}
      whileTap={{ scale: 0.98 }}
      className="group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCategoryColor(decision.category)} flex items-center justify-center text-2xl shadow-lg`}>
              {getCategoryIcon(decision.category)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors mb-1">
                {decision.title}
              </h3>
              <div className="flex items-center space-x-2 text-slate-400 text-sm">
                <Clock size={14} />
                <span className="capitalize">{decision.category}</span>
              </div>
            </div>
          </div>
          
          <motion.div
            whileHover={{ x: 4 }}
            className="text-slate-400 group-hover:text-blue-400 transition-colors"
          >
            <ChevronRight size={20} />
          </motion.div>
        </div>

        {/* Description */}
        <p className="text-slate-300 leading-relaxed mb-6 flex-1">
          {decision.description}
        </p>

        {/* Cost Indicators */}
        {hasExpensiveOptions && (
          <div className="mb-4 p-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl">
            <div className="flex items-center space-x-2 text-amber-400">
              <AlertCircle size={16} />
              <span className="text-sm font-medium">High-value decision</span>
            </div>
            <div className="text-xs text-amber-300 mt-1">
              Some options require significant investment
            </div>
          </div>
        )}

        {/* Options Preview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-400">Available Options:</h4>
            <span className="text-xs text-slate-500">{decision.options.length} choices</span>
          </div>
          
          <div className="space-y-2">
            {decision.options.slice(0, 2).map((option, index) => (
              <div key={option.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2 text-slate-300">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  <span className="truncate">{option.title}</span>
                </div>
                {option.cost && (
                  <div className={`text-xs font-medium px-2 py-1 rounded-lg ${
                    option.cost > 10000 
                      ? 'bg-red-500/20 text-red-400' 
                      : option.cost > 1000
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    ${option.cost.toLocaleString()}
                  </div>
                )}
              </div>
            ))}
            {decision.options.length > 2 && (
              <div className="text-xs text-slate-500 flex items-center space-x-1">
                <Star size={12} />
                <span>+{decision.options.length - 2} more options</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Indicator */}
        <motion.div 
          className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        >
          <div className="flex items-center space-x-2 text-blue-400 text-sm font-medium">
            <Zap size={14} />
            <span>Click to decide</span>
          </div>
          <div className="text-slate-500 text-xs">
            {decision.options.length} choices
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DecisionCard;